import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { Modal, Form } from 'react-bootstrap'
import {
  Button,
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import API from '../../utils/API'
import ScheduleDatePicker from './ScheduleDatePicker'
import TimeSelection from '../form/TimeSelection'
import { PlusIcon, DeleteIcon } from '../assetsComponents/common'
import ConflictWarningModal from './ConflictWarningModal'

const DateOverrideModal = ({
  show,
  onHide,
  selectedDates,
  onDatesChange,
  timeRanges,
  onAddTimeRange,
  onRemoveTimeRange,
  onTimeSelectionUpdate,
  isUnavailable,
  onUnavailableToggle,
  onSaveSuccess,
  currentOverrides = [],
  editingOverrideIndex = null,
  timezone,
  onConflictContinue,
  // AddIcon,
}) => {
  const [isSaving, setIsSaving] = useState(false)
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false)
  const [showConflictModal, setShowConflictModal] = useState(false)
  const [conflictData, setConflictData] = useState(null)
  const [conflictDate, setConflictDate] = useState(null)
  const Error = useErrorService()
  const Notification = useNotificationService()
  const { user } = useUserService()
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Initialize to current month or selected date's month
    if (selectedDates && selectedDates.length > 0) {
      return moment(selectedDates[0]).startOf('month')
    }
    return moment().startOf('month')
  })

  const getOverrideMinFromTime = (rangeIndex) => {
    if (rangeIndex === 0) return null
    return timeRanges[rangeIndex - 1]?.end || null
  }

  // Disable right side content when no date is selected
  const isRightSideDisabled = !selectedDates || selectedDates.length === 0

  // Check if selected date is today (current date)
  const isCurrentDate = () => {
    if (!selectedDates || selectedDates.length === 0 || !timezone) {
      return false
    }
    const selectedDate = selectedDates[0]
    const selectedDateStr = moment(selectedDate).format('YYYY-MM-DD')
    const todayStr = moment().tz(timezone).format('YYYY-MM-DD')
    return selectedDateStr === todayStr
  }

  const isToday = isCurrentDate()

  // Reset currentMonth to current month when modal opens for new override
  useEffect(() => {
    if (show) {
      // If no dates are selected (new override), reset to current month
      if (!selectedDates || selectedDates.length === 0) {
        setCurrentMonth(moment().startOf('month'))
      } else {
        // If dates are selected, set to the selected date's month
        setCurrentMonth(moment(selectedDates[0]).startOf('month'))
        // Trigger onDatesChange to load override data for the pre-selected date
        onDatesChange(selectedDates)
      }
    }
    // Only run when modal opens/closes, not when selectedDates changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  // Handle single date selection and convert to array format for parent component
  const handleDateSelect = (dateStr) => {
    // Convert single date to array format expected by parent
    const dateMoment = moment(dateStr)
    onDatesChange([dateMoment.toISOString()])
  }

  // const AddIconComponent = AddIcon || DefaultAddIcon

  const buildOverridesPayload = () => {
    // Create override for the selected date (single date selection)
    const selectedDate =
      selectedDates && selectedDates.length > 0 ? selectedDates[0] : null
    if (!selectedDate || !timezone) return []

    const selectedDateMoment = moment(selectedDate)
    const dateStr = selectedDateMoment.format('YYYY-MM-DD')

    // Get all existing overrides, excluding the ones for the selected date (if editing)
    let otherOverrides = []
    if (editingOverrideIndex !== null) {
      // When editing, remove ALL overrides for the selected date
      // Use timezone-aware date comparison to match what we do in handleDatesChange
      const selectedDateStr = moment(selectedDate).format('YYYY-MM-DD')
      otherOverrides = currentOverrides.filter((override) => {
        let overrideDateStr = null

        // Check if override is in the old format (startDate/endDate)
        if (override.startDate && override.endDate) {
          const overrideStart = moment(override.startDate).format('YYYY-MM-DD')
          const overrideEnd = moment(override.endDate).format('YYYY-MM-DD')
          // For old format, check if selected date falls within the range
          // But also check if it's exactly the same date
          if (
            selectedDateStr >= overrideStart &&
            selectedDateStr <= overrideEnd
          ) {
            return false // Exclude this override
          }
          return true // Keep this override
        }

        // Check if override is in the new format (start/end UTC timestamps)
        if (override.start && override.end) {
          // Use timezone-aware date comparison if timezone is available
          if (override.timezone !== undefined) {
            overrideDateStr = moment
              .utc(override.start)
              .tz(override.timezone)
              .format('YYYY-MM-DD')
          } else {
            // Fallback: use UTC date
            overrideDateStr = moment.utc(override.start).format('YYYY-MM-DD')
          }

          // Exclude if dates match
          if (overrideDateStr === selectedDateStr) {
            return false // Exclude this override
          }
        }

        // Keep override if we couldn't determine the date or it doesn't match
        return true
      })
    } else {
      // When adding new, keep all existing overrides
      otherOverrides = currentOverrides
    }

    const newOverrides = []

    if (isUnavailable) {
      // For unavailable: create a single override with start and end at 00:00:00 UTC for that date
      const unavailableStart = moment
        .tz(dateStr, timezone)
        .startOf('day')
        .utc()
        .toISOString()
      const unavailableEnd = moment
        .tz(dateStr, timezone)
        .startOf('day')
        .utc()
        .toISOString()

      newOverrides.push({
        start: unavailableStart,
        end: unavailableEnd,
        timezone: timezone,
        isActive: false,
      })
    } else {
      // For available: create one override per time range
      const validTimeRanges = timeRanges.filter(
        (range) => range.start && range.end,
      )

      validTimeRanges.forEach((range) => {
        // Parse time strings (format: "HH:mm" or "H:mm")
        const [startHour, startMinute] = range.start.split(':').map(Number)
        const [endHour, endMinute] = range.end.split(':').map(Number)

        // Create moment in the selected timezone for the selected date
        const startMoment = moment
          .tz(dateStr, timezone)
          .hour(startHour)
          .minute(startMinute || 0)
          .second(0)
          .millisecond(0)

        const endMoment = moment
          .tz(dateStr, timezone)
          .hour(endHour)
          .minute(endMinute || 0)
          .second(0)
          .millisecond(0)

        // If end time is before start time, assume it's next day
        if (endMoment.isBefore(startMoment)) {
          endMoment.add(1, 'day')
        }

        // Convert to UTC ISO strings
        const startUTC = startMoment.utc().toISOString()
        const endUTC = endMoment.utc().toISOString()

        newOverrides.push({
          start: startUTC,
          end: endUTC,
          timezone: timezone,
          isActive: true,
        })
      })
    }

    // Combine other overrides with new overrides
    // Convert old format overrides to new format if needed
    const convertedOtherOverrides = otherOverrides
      .map((override) => {
        // If already in new format, update timezone to current timezone
        // Remove _id field as it's not allowed in the payload
        if (override.start && override.end && override.timezone) {
          const { _id, id, ...overrideWithoutId } = override
          return {
            ...overrideWithoutId,
            timezone: timezone, // Update to current timezone
          }
        }

        // Convert old format to new format
        if (override.startDate && override.endDate) {
          const startDate = moment(override.startDate).format('YYYY-MM-DD')
          const endDate = moment(override.endDate).format('YYYY-MM-DD')

          if (!override.isActive) {
            // Unavailable: create single override
            return {
              start: moment.utc(startDate).startOf('day').toISOString(),
              end: moment.utc(endDate).startOf('day').toISOString(),
              timezone: timezone, // Use current header timezone for old data
              isActive: false,
            }
          } else if (override.timeRanges && override.timeRanges.length > 0) {
            // Available: create one override per time range
            return override.timeRanges
              .filter((range) => range.start && range.end)
              .map((range) => {
                const [startHour, startMinute] = range.start
                  .split(':')
                  .map(Number)
                const [endHour, endMinute] = range.end.split(':').map(Number)

                const startMoment = moment
                  .tz(startDate, timezone)
                  .hour(startHour)
                  .minute(startMinute || 0)
                  .second(0)
                  .millisecond(0)

                const endMoment = moment
                  .tz(endDate, timezone)
                  .hour(endHour)
                  .minute(endMinute || 0)
                  .second(0)
                  .millisecond(0)

                if (endMoment.isBefore(startMoment)) {
                  endMoment.add(1, 'day')
                }

                return {
                  start: startMoment.utc().toISOString(),
                  end: endMoment.utc().toISOString(),
                  timezone: timezone,
                  isActive: true,
                }
              })
          }
        }

        return null
      })
      .filter(Boolean)
      .flat()

    // Combine and deduplicate overrides
    const allOverrides = [...convertedOtherOverrides, ...newOverrides]

    // Remove duplicates based on start, end, timezone, and isActive
    const uniqueOverrides = []
    const seenOverrides = new Set()

    allOverrides.forEach((override) => {
      // Create a unique key for each override
      const key = `${override.start}|${override.end}|${override.timezone}|${override.isActive}`

      if (!seenOverrides.has(key)) {
        seenOverrides.add(key)
        uniqueOverrides.push(override)
      }
    })

    return uniqueOverrides
  }

  const checkConflicts = async (dateStr) => {
    if (!user?._id || !dateStr || !timezone) {
      return null
    }

    try {
      const { data } = await API.AdminAPI.scheduling.checkOverrideConflicts({
        coachId: user._id,
        date: dateStr,
        timezone: timezone,
      })
      return data
    } catch (error) {
      // If error, don't block - just log and continue
      console.error('Error checking conflicts:', error)
      return null
    }
  }

  const handleSave = async () => {
    if (!selectedDates || selectedDates.length === 0) {
      Notification.showNotification('Please select a date.', 'danger')
      return
    }

    if (!timezone) {
      Notification.showNotification('Timezone is required.', 'danger')
      return
    }

    if (!isUnavailable) {
      const hasValidTimeRange = timeRanges.some(
        (range) => range.start && range.end,
      )
      if (!hasValidTimeRange) {
        Notification.showNotification(
          'Please provide at least one time range or mark as unavailable.',
          'danger',
        )
        return
      }
    }

    // Check for conflicts before saving (only if marking as unavailable)
    if (isUnavailable) {
      const selectedDate =
        selectedDates && selectedDates.length > 0 ? selectedDates[0] : null
      if (selectedDate && timezone) {
        setIsCheckingConflicts(true)
        try {
          const dateStr = moment(selectedDate).format('YYYY-MM-DD')
          const conflictResult = await checkConflicts(dateStr)

          if (conflictResult?.hasConflict && conflictResult?.count > 0) {
            // Show conflict modal
            setConflictData(conflictResult)
            setConflictDate(dateStr)
            setShowConflictModal(true)
            return // Don't save yet, wait for user decision
          }
        } catch (error) {
          console.error('Error checking conflicts:', error)
          // Continue with save even if conflict check fails
        } finally {
          setIsCheckingConflicts(false)
        }
      }
    }

    // No conflicts or not unavailable, proceed with save
    await performSave()
  }

  const performSave = async () => {
    setIsSaving(true)
    try {
      const overridesPayload = buildOverridesPayload()

      // Send only overrides payload
      const payload = {
        overrides: overridesPayload,
      }

      const { data } = await API.AdminAPI.scheduling.updateCalendarAvailability(
        payload,
      )

      // Notify parent component about successful save
      if (onSaveSuccess) {
        onSaveSuccess(data)
      }

      Notification.showNotification(
        editingOverrideIndex !== null
          ? 'Date override updated successfully.'
          : 'Date override saved successfully.',
        'success',
      )

      // Close modal after successful save
      onHide()
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleConflictContinue = () => {
    setShowConflictModal(false)
    // User wants to go to scheduled tab to resolve conflicts
    // Don't save the override, just redirect and close modal
    if (onConflictContinue && conflictData) {
      const selectedDate =
        selectedDates && selectedDates.length > 0 ? selectedDates[0] : null
      if (selectedDate) {
        const dateStr = moment(selectedDate).format('YYYY-MM-DD')
        onConflictContinue(dateStr, conflictData.events || [])
        // Close the modal so user can see the scheduled tab
        onHide()
      }
    }
  }

  const handleConflictCancel = () => {
    setShowConflictModal(false)
    // User cancelled, just close the conflict modal without saving
    // Don't uncheck checkbox or close main modal, let user decide
  }

  const handleUnavailableToggle = (event) => {
    // Just toggle the checkbox, no conflict check here
    if (onUnavailableToggle) {
      onUnavailableToggle(event)
    }
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size='lg'
        centered
        backdrop='static'
        keyboard={false}
        className='payment-modal date-override-modal'
        style={{
          zIndex: showConflictModal ? 1056 : 1050,
        }}>
        {showConflictModal && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 1,
              borderRadius: '0.3rem',
              pointerEvents: 'none',
            }}
          />
        )}
        <div
          style={{
            position: 'relative',
            opacity: showConflictModal ? 0.3 : 1,
            pointerEvents: showConflictModal ? 'none' : 'auto',
            transition: 'opacity 0.3s ease',
          }}>
          <Modal.Header className='payment-modal-header date-override-modal__header d-flex justify-content-center'>
            <h3 className='payment-modal-title mb-0 text-uppercase'>
              {editingOverrideIndex !== null
                ? 'Edit date override'
                : 'Select the date to override'}
            </h3>
          </Modal.Header>
          <Modal.Body className='payment-modal-body date-override-modal__body'>
            <div className='date-override-modal__content'>
              <div className='date-override-modal__calendar'>
                <ScheduleDatePicker
                  currentMonth={currentMonth}
                  selectedDate={
                    selectedDates && selectedDates.length > 0
                      ? selectedDates[0]
                      : null
                  }
                  onMonthChange={setCurrentMonth}
                  onDateSelect={handleDateSelect}
                  multiple={false}
                  disablePastDates={true}
                />
              </div>
              <div className='date-override-modal__settings'>
                <h6 className='date-override-modal__question'>
                  Which hours are you free?
                </h6>
                <div>
                  {timeRanges.map((range, index) => (
                    <div
                      key={`time-range-${index}`}
                      className='d-flex align-items-center mb-2'>
                      <TimeSelection
                        fromTime={range.start || ''}
                        toTime={range.end || ''}
                        onTimeChange={(from, to) =>
                          onTimeSelectionUpdate(index, from, to)
                        }
                        usdedInScheduling={true}
                        handleBlur={() => {}}
                        minFromTime={getOverrideMinFromTime(index)}
                        disabled={
                          isRightSideDisabled || isUnavailable || isToday
                        }
                        hideDisabledOptions={true}
                      />
                      <button
                        type='button'
                        className='availability-icon-btn mx-2'
                        onClick={() => onRemoveTimeRange(index)}
                        aria-label='Remove time range'
                        disabled={
                          isRightSideDisabled ||
                          timeRanges.length === 1 ||
                          isUnavailable ||
                          isToday
                        }>
                        <DeleteIcon />
                      </button>
                      <button
                        type='button'
                        className='availability-icon-btn'
                        onClick={onAddTimeRange}
                        aria-label='Add time range'
                        disabled={
                          isRightSideDisabled || isUnavailable || isToday
                        }>
                        <PlusIcon />
                      </button>
                    </div>
                  ))}
                </div>
                <div className='date-override-modal__toggle-label'>
                  <Form.Check
                    type='checkbox'
                    id='mark-unavailable-checkbox'
                    checked={isUnavailable}
                    onChange={handleUnavailableToggle}
                    disabled={isRightSideDisabled || isToday}
                    label='Mark unavailable (All day)'
                    custom
                    className='cursor-pointer'
                  />
                </div>
                {isToday && (
                  <p className='text-danger mt-2 mb-0 sa5'>
                    You cannot modify overrides for the current date.
                  </p>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex justify-content-end'>
              <Button
                variant='outline-dark'
                className='mr-3 text-uppercase s8'
                onClick={onHide}
                disabled={isSaving}>
                Cancel
              </Button>
              <Button
                variant='saffron'
                className='save-btn-fixed-width'
                onClick={handleSave}
                isLoading={isSaving || isCheckingConflicts}
                disabled={
                  isSaving ||
                  isCheckingConflicts ||
                  isRightSideDisabled ||
                  isToday
                }>
                {editingOverrideIndex !== null
                  ? 'Update Override'
                  : 'Save Override'}
              </Button>
            </div>
          </Modal.Footer>
        </div>
      </Modal>
      <ConflictWarningModal
        show={showConflictModal}
        handleClose={handleConflictCancel}
        onContinue={handleConflictContinue}
        conflictCount={conflictData?.count || 0}
        date={conflictDate}
      />
    </>
  )
}

DateOverrideModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedDates: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  ).isRequired,
  onDatesChange: PropTypes.func.isRequired,
  timeRanges: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  ).isRequired,
  onAddTimeRange: PropTypes.func.isRequired,
  onRemoveTimeRange: PropTypes.func.isRequired,
  onTimeSelectionUpdate: PropTypes.func.isRequired,
  isUnavailable: PropTypes.bool.isRequired,
  onUnavailableToggle: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  currentOverrides: PropTypes.array,
  editingOverrideIndex: PropTypes.number,
  timezone: PropTypes.string,
  onConflictContinue: PropTypes.func,
  AddIcon: PropTypes.elementType,
}

export default DateOverrideModal
